import { graphHooks } from '@tunasong/graph-lib/react'
import { profilesApi } from '@tunasong/redux'
import {
  Button,
  RuleCard,
  isInboxRule,
  readRules,
  removeRule,
  resumeRule,
  setRuleEnabled,
  setRuleTag,
  starterInboxRule,
  upsertRule,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './inbox-automations.module.css'

/**
 * Frontend availability flag for the Inbox convert action. Stays false until the
 * engine's `INBOX_RULES_ENABLED` flag flips and the server handler is live — the
 * builder is fully usable meanwhile; only the enable switch is gated (spec §3).
 */
const INBOX_ACTION_AVAILABLE = false

export const InboxAutomations: FC = () => {
  const { profile } = graphHooks.useProfile()
  const [updateProfile] = profilesApi.useUpdateProfileMutation()
  const config = profile?.config
  const inboxRules = readRules(config).filter(isInboxRule)

  const save = (nextConfig: Record<string, unknown>) => {
    if (!profile) {
      return
    }
    void updateProfile({ id: profile.id, profile: { config: nextConfig } })
  }

  return (
    <section className={styles.section} aria-label="Inbox automations">
      <header className={styles.header}>
        <h3 className={styles.heading}>Inbox</h3>
        <p className={styles.sub}>Automate what happens to things you capture.</p>
      </header>

      {inboxRules.length === 0 ? (
        <div className={styles.starter} role="group" aria-label="Turn tagged notes into Songs">
          <div>
            <strong>Turn tagged notes into Songs</strong>
            <p className={styles.sub}>When you tag a note song, convert it to a Song automatically.</p>
          </div>
          <Button onClick={() => save(upsertRule(config, starterInboxRule()))}>Set up</Button>
        </div>
      ) : (
        inboxRules.map(rule => (
          <RuleCard
            key={rule.id}
            rule={rule}
            actionAvailable={INBOX_ACTION_AVAILABLE}
            onToggle={enabled => save(setRuleEnabled(config, rule.id, enabled))}
            onTagChange={tag => save(setRuleTag(config, rule.id, tag))}
            onResume={() => save(resumeRule(config, rule.id))}
            onDelete={() => save(removeRule(config, rule.id))}
          />
        ))
      )}
    </section>
  )
}
